import EnrollmentCardsSection from 'components/common/enrollment/EnrollmentCardsSection';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import { EnrollmentType } from 'types/enrollmentService';
import { OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { createStyles } from './styles';
import QuotesTable from '../QuotesTable';
import EStoreEnrollmentCardsSection from '../EStoreEnrollmentCardsSection/index';
import { NoDataPlaceholderStyled } from '../styles';

interface Props {
  employerGroupId: string;
}

const EmployerGroupProfileContent: React.FC<Props> = observer(({ employerGroupId }) => {
  const classes = createStyles();

  const { groupId } = useParams<{ groupId: string }>();

  const {
    enrollmentCardStore: { loading, activePeriods, collectEnrollmentPeriods, resetInitialData },
    groupProposalsTableStore: {
      loading: tableLoading,
      groupProposalsPerPeriod,
      buildProposalsList,
      resetProposalsData,
    },
  } = useRootStore();

  const activeEnrollmentPeriods = activePeriods.map((period) => toJS(period)).slice();
  const convertedGroupProposalsPerPeriod = groupProposalsPerPeriod.map((dateObj) => toJS(dateObj)).slice();

  useEffect(() => {
    void collectEnrollmentPeriods(employerGroupId || groupId);
    void buildProposalsList(employerGroupId || groupId);

    return (): void => {
      resetInitialData();
      resetProposalsData();
    };
  }, []);

  // New sale enrollments with confirmed proposals
  const activeNewSaleEnrollmentPeriods = activeEnrollmentPeriods
    .filter((enrollmentPeriod) => enrollmentPeriod.hasConfirmedProposal)
    .filter(
      (enrollmentPeriod) =>
        enrollmentPeriod.type === EnrollmentType.NEW_SALE || enrollmentPeriod.type === EnrollmentType.RESUBMIT_NEW_SALE,
    );

  // Active enrollments with preferred proposals
  const activeEStoreEnrollmentPeriods =
    activeNewSaleEnrollmentPeriods.length === 0
      ? activeEnrollmentPeriods.filter((enrollmentPeriod) =>
          enrollmentPeriod.proposals.some((proposal) => proposal?.preferred),
        )
      : [];

  const hideActiveEStoreQuote = activeEStoreEnrollmentPeriods.length > 0 && activeNewSaleEnrollmentPeriods.length === 0;

  // For hideActiveEStoreQuote : true-> Exclude proposals with the 'preferred' flag set to true,
  // as this information will be included in the quote enrollment card.
  const filteredProposalsPerPeriod = hideActiveEStoreQuote
    ? convertedGroupProposalsPerPeriod.filter((proposalPerPeriod) => !proposalPerPeriod?.proposal?.preferred)
    : convertedGroupProposalsPerPeriod;

  // Show alternative periods only when there are no confirmed proposals and alternative proposals list is not empty .
  const showAlternativeQuotes = activeNewSaleEnrollmentPeriods.length === 0 && filteredProposalsPerPeriod.length > 0;
  const showNoDataPlaceholder =
    activeNewSaleEnrollmentPeriods.length === 0 && activeEStoreEnrollmentPeriods.length === 0 && !showAlternativeQuotes;

  return (
    <div className={classes.employerContent}>
      {loading || tableLoading ? (
        <OverlaySpinner display overlay="light" />
      ) : (
        <>
          {activeNewSaleEnrollmentPeriods.length > 0 && (
            // Redirect to checklist
            <EnrollmentCardsSection
              cardsList={activeNewSaleEnrollmentPeriods}
              loading={loading}
              sectionTitle="Current enrollments"
              type="ee"
            />
          )}
          {activeEStoreEnrollmentPeriods.length > 0 && (
            // Redirect to proposal
            <EStoreEnrollmentCardsSection cardsList={activeEStoreEnrollmentPeriods} />
          )}
          {/* Don't show alternative proposals if there is a confirmed proposal or alternative proposals list is empty */}
          {showAlternativeQuotes && (
            <section className={classes.section}>
              <h3 className={classes.sectionTitle}>
                {activeEStoreEnrollmentPeriods.length > 0 ? 'Alternative quotes' : 'Quotes'}
              </h3>
              <QuotesTable groupProposals={filteredProposalsPerPeriod} />
            </section>
          )}
          {showNoDataPlaceholder && (
            <NoDataPlaceholderStyled
              title="No enrollment available"
              text="Once a proposal has been confirmed, you will be able to start your enrollment"
            />
          )}
        </>
      )}
    </div>
  );
});

export default EmployerGroupProfileContent;
